import type { CellChange, Command, PropState } from "../document/commands.ts";
import { staleIgnoreKeys } from "../document/issue-ignores.ts";
import { isLayoutCommand } from "../document/layout-commands.ts";
import { framesWithout } from "../document/tileset-frames.ts";
import type { WorldDocument } from "../document/types.ts";
import { layoutApplyLabel, layoutLabel, mapRemovalLabel } from "./layout-labels.ts";

/**
 * The History drawer's words for a command (ADR-070's amendment "the drawers", decision 5): what the command
 * did, in the chrome's words (props, markers, layers), naming a layer by its name in the document now, by the
 * command's own record for a layer it adds or removes, or by its id when the layer is gone from the document.
 * A compound of one kind is counted ("Placed 3 props on Props"); one of fills, with the layer changes a
 * terrain replace carries, is a replace (ADR-097); a map removed with its seams is the map's removal (ADR-109
 * decision 6); a mixed one names its first part and counts the rest.
 */

/**
 * A count with its noun, singular for one.
 *
 * @param count How many.
 * @param noun The singular noun.
 * @returns For example "1 cell" or "3 cells".
 */
function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * What a change of a map's ignored issues did (ADR-070's amendment of 2026-10-05): how many it ignored, how
 * many it stopped ignoring, or, when every record it removed names a thing the map no longer holds, how many
 * stale ones it dropped. Stale records dropped along with an ignore or a stop are not counted in its words.
 *
 * @param command The command.
 * @param document The document now.
 * @returns The label.
 */
function ignoresLabel(command: Extract<Command, { readonly kind: "setIssueIgnores" }>, document: WorldDocument): string {
  const added = Object.keys(command.after).filter((key) => !Object.hasOwn(command.before, key));
  const gone = Object.keys(command.before).filter((key) => !Object.hasOwn(command.after, key));
  const map = document.maps.find((candidate) => candidate.id === command.mapId);
  const stale = map === undefined ? [] : staleIgnoreKeys({ ...map, ignoredIssues: command.before });
  const stopped = gone.filter((key) => !stale.includes(key));
  if (added.length > 0 && stopped.length > 0) {
    return "Changed the ignored issues";
  }
  if (added.length > 0) {
    return `Ignored ${counted(added.length, "issue")}`;
  }
  if (stopped.length > 0) {
    return `Stopped ignoring ${counted(stopped.length, "issue")}`;
  }
  return gone.length === 0 ? "Changed the ignored issues" : `Dropped ${counted(gone.length, "ignore")} of things no longer on the map`;
}

/**
 * A layer's name in the document, or its id when the document no longer holds it.
 *
 * @param document The document now.
 * @param mapId The map's id.
 * @param layerId The layer's id.
 * @returns The name.
 */
function layerName(document: WorldDocument, mapId: string, layerId: string): string {
  return document.maps.find((m) => m.id === mapId)?.layers.find((l) => l.id === layerId)?.name ?? layerId;
}

/**
 * Whether a layer is a terrain layer, whose grid is of corners.
 *
 * @param document The document now.
 * @param mapId The map's id.
 * @param layerId The layer's id.
 * @returns True for a terrain layer.
 */
function isTerrain(document: WorldDocument, mapId: string, layerId: string): boolean {
  return document.maps.find((m) => m.id === mapId)?.layers.find((l) => l.id === layerId)?.kind === "terrain";
}

/**
 * Whether every cell of a stroke ends empty.
 *
 * @param cells The stroke's cells.
 * @returns True when each cell's value after is zero.
 */
function erases(cells: readonly CellChange[]): boolean {
  return cells.length > 0 && cells.every((c) => c.after === 0);
}

/**
 * The fields of a prop's state an edit changed.
 *
 * @param before The state before.
 * @param after The state after.
 * @returns The changed parts, each once.
 */
function propChanges(before: PropState, after: PropState): readonly ("place" | "flip" | "sort" | "frame" | "behaviour" | "keep" | "release" | "footprint")[] {
  const parts: ("place" | "flip" | "sort" | "frame" | "behaviour" | "keep" | "release" | "footprint")[] = [];
  if (before.x !== after.x || before.y !== after.y) {
    parts.push("place");
  }
  if (before.flipX !== after.flipX || before.flipY !== after.flipY) {
    parts.push("flip");
  }
  if (before.sortPixels !== after.sortPixels) {
    parts.push("sort");
  }
  if (before.frame.tileset !== after.frame.tileset || before.frame.tile !== after.frame.tile) {
    parts.push("frame");
  }
  if (JSON.stringify(before.behaviour ?? null) !== JSON.stringify(after.behaviour ?? null)) {
    parts.push("behaviour");
  }
  if ((before.keepPlacement === true) !== (after.keepPlacement === true)) {
    parts.push(after.keepPlacement === true ? "keep" : "release");
  }
  if (JSON.stringify(before.footprint ?? null) !== JSON.stringify(after.footprint ?? null) && parts.length === 0) {
    parts.push("footprint");
  }
  return parts;
}

const PROP_EDIT_WORDS: Readonly<Record<"place" | "flip" | "sort" | "frame" | "behaviour" | "keep" | "release" | "footprint", string>> = { place: "Moved prop", flip: "Flipped prop", sort: "Changed prop sort offset", frame: "Changed prop frame", behaviour: "Changed prop behaviour", keep: "Kept prop as placed", release: "Stopped keeping prop as placed", footprint: "Changed prop's own collision" };

/**
 * The words for a prop edit: its one changed part, or "Edited prop" for several or none.
 *
 * @param before The state before.
 * @param after The state after.
 * @returns The words.
 */
function propEditLabel(before: PropState, after: PropState): string {
  const parts = propChanges(before, after);
  const only = parts.length === 1 ? parts[0] : undefined;
  return only === undefined ? "Edited prop" : PROP_EDIT_WORDS[only];
}

/**
 * The words for a compound whose parts all share one kind, or null when a count says nothing clearer than
 * the first part's words.
 *
 * @param commands The parts, two or more.
 * @param document The document now.
 * @returns The words, or null.
 */
function sameKindLabel(commands: readonly Command[], document: WorldDocument): string | null {
  const first = commands[0] as Command;
  if (!commands.every((c) => c.kind === first.kind)) {
    return null;
  }
  const count = commands.length;
  switch (first.kind) {
    case "addProp":
      return commands.every((c) => c.kind === "addProp" && c.layerId === first.layerId) ? `Placed ${count} props on ${layerName(document, first.mapId, first.layerId)}` : `Placed ${count} props`;
    case "removeProp":
      return commands.every((c) => c.kind === "removeProp" && c.layerId === first.layerId) ? `Removed ${count} props from ${layerName(document, first.mapId, first.layerId)}` : `Removed ${count} props`;
    case "updateProp":
      return commands.every((c) => c.kind === "updateProp" && propEditLabel(c.before, c.after) === "Moved prop") ? `Moved ${count} props` : `Edited ${count} props`;
    case "addEntity":
      return `Placed ${count} markers`;
    case "removeEntity":
      return `Removed ${count} markers`;
    case "updateEntity":
      return `Edited ${count} markers`;
    default:
      return null;
  }
}

/**
 * The layer a part of a replace writes.
 *
 * @param command The part.
 * @returns The layer's id, or null for a part no replace holds or a move that names no layer.
 */
function replacedLayer(command: Command): string | null {
  switch (command.kind) {
    case "fillCells":
    case "setLayerTerrain":
    case "setLayerGroup":
    case "updateProp":
      return command.layerId;
    case "moveLayer":
      return command.layerId ?? null;
    case "addLayer":
      return command.layer.id;
    default:
      return null;
  }
}

/**
 * The words for a replace (ADR-097): a compound of fills, with the terrain change, the added layer, the move
 * and the group change a terrain replace carries and the prop updates a prop replace carries, holding at least
 * one fill or terrain change. Fills alone on
 * one layer are counted; a terrain replace on one layer is named for it; several layers are counted.
 *
 * @param commands The parts, one or more.
 * @param document The document now.
 * @returns The words, or null when the compound is not a replace.
 */
function replaceLabel(commands: readonly Command[], document: WorldDocument): string | null {
  const first = commands[0];
  const layers = new Set<string>();
  for (const command of commands) {
    const layerId = replacedLayer(command);
    if (layerId === null) {
      return null;
    }
    layers.add(layerId);
  }
  if (first === undefined || first.kind === "compound" || !commands.some((c) => c.kind === "fillCells" || c.kind === "setLayerTerrain")) {
    return null;
  }
  if (layers.size > 1) {
    return `Replaced on ${layers.size} layers`;
  }
  const layerId = [...layers][0] as string;
  const mapId = "mapId" in first ? first.mapId : "";
  const fills = commands.filter((c): c is Extract<Command, { readonly kind: "fillCells" }> => c.kind === "fillCells");
  if (fills.length !== commands.length) {
    return `Replaced terrain on ${layerName(document, mapId, layerId)}`;
  }
  const changed = fills.reduce((sum, c) => sum + c.indices.length, 0);
  return `Replaced on ${layerName(document, mapId, layerId)} (${counted(changed, isTerrain(document, mapId, layerId) ? "corner" : "cell")})`;
}

/**
 * The words for a compound.
 *
 * @param commands The parts.
 * @param document The document now.
 * @returns The words.
 */
function compoundLabel(commands: readonly Command[], document: WorldDocument): string {
  const first = commands[0];
  if (first === undefined) {
    return "No change";
  }
  const replaced = replaceLabel(commands, document) ?? mapRemovalLabel(commands) ?? layoutApplyLabel(commands);
  if (replaced !== null) {
    return replaced;
  }
  if (commands.length === 1) {
    return commandLabel(first, document);
  }
  return layerMoveLabel(commands, document) ?? sameKindLabel(commands, document) ?? `${commandLabel(first, document)} and ${counted(commands.length - 1, "more change")}`;
}

/**
 * The words for props moved to another layer (P1.6-T51): removals, then additions to one layer of the same
 * props by id, as `movePropsToLayerCommand` builds them; null for any other compound.
 *
 * @param commands The parts, two or more.
 * @param document The document now.
 * @returns The words, or null.
 */
function layerMoveLabel(commands: readonly Command[], document: WorldDocument): string | null {
  const removed = commands.filter((c) => c.kind === "removeProp");
  const added = commands.slice(removed.length);
  const target = added[0];
  if (removed.length === 0 || removed.length !== added.length || target?.kind !== "addProp" || !added.every((c) => c.kind === "addProp" && c.layerId === target.layerId)) {
    return null;
  }
  const out = new Set(removed.map((c) => (c.kind === "removeProp" ? c.prop.id : "")));
  if (!added.every((c) => c.kind === "addProp" && out.has(c.prop.id))) {
    return null;
  }
  const where = layerName(document, target.mapId, target.layerId);
  return added.length === 1 ? `Moved prop to ${where}` : `Moved ${added.length} props to ${where}`;
}

/**
 * The words for a change of the tileset list: how many whole tilesets it added or removed, or how many frames
 * it pulled or took out again (ADR-129 decision 3.4). A change that does more than one of the four is "Changed
 * the tilesets".
 *
 * @param command The command.
 * @returns The words.
 */
function tilesetsLabel(command: Extract<Command, { readonly kind: "changeTilesets" }>): string {
  const frames = command.frames ?? [];
  let pulled = command.insert.reduce((sum, entry) => sum + (entry.frames?.length ?? 0), 0);
  let dropped = command.remove.reduce((sum, entry) => sum + (entry.frames?.length ?? 0), 0);
  let added = command.insert.filter((entry) => entry.frames === undefined).length;
  const removed = command.remove.filter((entry) => entry.frames === undefined).length;
  let other = 0;
  for (const change of frames) {
    if (change.before === undefined) {
      other += 1;
    } else if (change.after === undefined) {
      added += 1;
    } else {
      pulled += framesWithout(change.after, change.before).length;
      dropped += framesWithout(change.before, change.after).length;
    }
  }
  const done = [pulled, dropped, added, removed].filter((count) => count > 0).length;
  if (other > 0 || done !== 1) {
    return "Changed the tilesets";
  }
  if (pulled > 0) {
    return `Pulled ${counted(pulled, "frame")}`;
  }
  if (dropped > 0) {
    return `Removed ${dropped} pulled frame${dropped === 1 ? "" : "s"}`;
  }
  return added > 0 ? `Added ${counted(added, "tileset")}` : `Removed ${counted(removed, "tileset")}`;
}

/**
 * The words for a command that changes the world rather than one map's layers.
 *
 * @param command The command.
 * @param document The document now.
 * @returns The words, or null for a command over a map's layers.
 */
function worldLabel(command: Command, document: WorldDocument): string | null {
  if (isLayoutCommand(command)) {
    return layoutLabel(command, document);
  }
  switch (command.kind) {
    case "compound":
      return compoundLabel(command.commands, document);
    case "changeTilesets":
      return tilesetsLabel(command);
    case "setTerrainName":
      return command.after === undefined ? `Cleared the name ${command.before ?? command.entry}` : `Named terrain ${command.after}`;
    case "addMap":
      return `Added map ${command.map.name}`;
    case "removeMap":
      return `Removed map ${command.map.name}`;
    case "renameMap":
      return `Renamed map ${command.before} to ${command.after}`;
    case "setMapProperties":
      return `Changed the fields of map ${document.maps.find((m) => m.id === command.mapId)?.name ?? command.mapId}`;
    case "setIssueIgnores":
      return ignoresLabel(command, document);
    default:
      return null;
  }
}

/**
 * A command's words.
 *
 * @param command The command.
 * @param document The document now, which names the layers.
 * @returns The words.
 */
export function commandLabel(command: Command, document: WorldDocument): string {
  const world = worldLabel(command, document);
  if (world !== null) {
    return world;
  }
  switch (command.kind) {
    case "paintCorners":
      return `Painted terrain on ${layerName(document, command.mapId, command.layerId)} (${counted(command.cells.length, "corner")})`;
    case "paintCollision":
      return `${erases(command.cells) ? "Erased" : "Painted"} collision on ${layerName(document, command.mapId, command.layerId)} (${counted(command.cells.length, "cell")})`;
    case "paintZone":
      return `${command.cells.length > 0 && command.cells.every((c) => c.after === null) ? "Erased" : "Painted"} zones on ${layerName(document, command.mapId, command.layerId)} (${counted(command.cells.length, "cell")})`;
    case "setTiles":
      return `${command.cells.length > 0 && command.cells.every((c) => c.after === null) ? "Erased" : "Painted"} tiles on ${layerName(document, command.mapId, command.layerId)} (${counted(command.cells.length, "cell")})`;
    case "fillCells":
      return `Filled ${layerName(document, command.mapId, command.layerId)} (${counted(command.indices.length, isTerrain(document, command.mapId, command.layerId) ? "corner" : "cell")})`;
    case "addLayer":
      return `Added layer ${command.layer.name}`;
    case "removeLayer":
      return `Removed layer ${command.layer.name}`;
    case "renameLayer":
      return `Renamed layer ${command.before} to ${command.after}`;
    case "addGroup":
      return `Added group ${command.group.name}`;
    case "removeGroup":
      return `Removed group ${command.group.name}`;
    case "renameGroup":
      return `Renamed group ${command.before} to ${command.after}`;
    case "moveLayer":
      return "Reordered layers";
    case "setPlayersAbove":
      return "Moved the Players row";
    case "setLayerGroup":
      return `Moved ${layerName(document, command.mapId, command.layerId)} ${command.after === undefined ? "out of its group" : "into a group"}`;
    case "setLayerTerrain":
      return `Changed the terrain of ${layerName(document, command.mapId, command.layerId)}`;
    case "setLayerFaceHeight":
      return `Changed the face height of ${layerName(document, command.mapId, command.layerId)}`;
    case "addProp":
      return `Placed prop on ${layerName(document, command.mapId, command.layerId)}`;
    case "removeProp":
      return `Removed prop from ${layerName(document, command.mapId, command.layerId)}`;
    case "updateProp":
      return propEditLabel(command.before, command.after);
    case "addEntity":
      return `Placed marker ${command.entity.type} on ${layerName(document, command.mapId, command.layerId)}`;
    case "removeEntity":
      return `Removed marker ${command.entity.type} from ${layerName(document, command.mapId, command.layerId)}`;
    case "updateEntity":
      return command.before.x !== command.after.x || command.before.y !== command.after.y ? "Moved marker" : "Edited marker fields";
    default:
      return "Changed the world";
  }
}
